import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Loader2, Clock, MapPin, LogIn, LogOut, Trash2, Users, Check, Search, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, fmtDateTime } from "@/lib/constants";
import { MonthPicker, currentMonth, monthRange, monthLabel } from "@/components/MonthPicker";
import { RiepilogoPdfButton, DistaccamentoFilter, ProvinciaFilter } from "@/components/RiepilogoPdf";

const STATO_TURNO = {
  pianificato: "bg-slate-100 text-slate-700 border-slate-200",
  "in servizio": "bg-amber-100 text-amber-800 border-amber-200",
  completato: "bg-green-100 text-green-800 border-green-200",
};

const MANAGE_ROLES = ["admin", "coordinatore", "territoriale", "com_distaccamento"];
const EMPTY_FORM = { data: "", ora_inizio: "08:00", ora_fine: "14:00", distaccamento: "", guardie_ids: [], note: "" };

export default function Turni() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const myGuardiaId = user?.guardia_id;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [guardSearch, setGuardSearch] = useState("");
  const [guardDistacc, setGuardDistacc] = useState("tutti");
  const [mese, setMese] = useState(currentMonth());
  const [pdfDistacc, setPdfDistacc] = useState("tutti");
  const [pdfProv, setPdfProv] = useState("tutte");
  const range = monthRange(mese);

  const { data: turni = [] } = useQuery({ queryKey: ["turni", mese], queryFn: async () => (await api.get("/turni", { params: range })).data });
  const { data: guardie = [] } = useQuery({ queryKey: ["guardie-select"], queryFn: async () => (await api.get("/guardie?stato=attiva")).data, enabled: canManage });
  const { data: distaccamenti = [] } = useQuery({ queryKey: ["distaccamenti"], queryFn: async () => (await api.get("/guardie/distaccamenti")).data, enabled: canManage });

  const guardieFiltrate = guardie.filter((g) => {
    const matchDist = guardDistacc === "tutti" || g.distaccamento === guardDistacc;
    const q = guardSearch.trim().toLowerCase();
    const matchName = !q || `${g.cognome} ${g.nome}`.toLowerCase().includes(q) || (g.codice_fiscale || "").toLowerCase().includes(q);
    return matchDist && matchName;
  });

  const toggleGuardia = (id) => setForm((f) => ({
    ...f, guardie_ids: f.guardie_ids.includes(id) ? f.guardie_ids.filter((x) => x !== id) : [...f.guardie_ids, id],
  }));

  const submit = async () => {
    if (!form.data || form.guardie_ids.length === 0) { toast.error("Data e almeno una guardia sono obbligatori"); return; }
    setSaving(true);
    try {
      if (editingId) { await api.put(`/turni/${editingId}`, form); } else { await api.post("/turni", form); }
      toast.success(editingId ? "Turno aggiornato" : "Turno pianificato");
      qc.invalidateQueries({ queryKey: ["turni"] });
      setOpen(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      setGuardSearch("");
      setGuardDistacc("tutti");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const action = async (tid, type, guardiaId) => {
    try {
      const qs = guardiaId ? `?guardia_id=${guardiaId}` : "";
      await api.post(`/turni/${tid}/${type}${qs}`);
      toast.success(type === "checkin" ? "Check-in registrato" : "Check-out registrato");
      qc.invalidateQueries({ queryKey: ["turni"] });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Eliminare il turno del ${fmtDate(t.data)} (${t.ora_inizio}–${t.ora_fine})?`)) { return; }
    try { await api.delete(`/turni/${t.id}`); toast.success("Turno eliminato"); qc.invalidateQueries({ queryKey: ["turni"] }); } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const startEdit = (t) => {
    setForm({ data: t.data, ora_inizio: t.ora_inizio, ora_fine: t.ora_fine, distaccamento: t.distaccamento || "", guardie_ids: (t.guardie || []).map((g) => g.guardia_id), note: t.note || "" });
    setEditingId(t.id);
    setOpen(true);
  };

  const onOpenChange = (o) => {
    setOpen(o);
    if (!o && editingId) { setEditingId(null); setForm(EMPTY_FORM); setGuardSearch(""); setGuardDistacc("tutti"); }
  };

  return (
    <div className="gz-fade-up">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Turni & presenze</h1>
          <p className="mt-1 text-sm text-slate-500">{canManage ? "Pianifica servizi con più guardie e gestisci le presenze." : "I tuoi turni e check-in/out."}</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTrigger asChild><Button className="bg-[#14382C] hover:bg-[#0E281F]" data-testid="new-turno-button"><Plus className="mr-2 h-4 w-4" /> Nuovo turno</Button></DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto gz-scroll">
              <DialogHeader><DialogTitle>{editingId ? "Modifica turno" : "Pianifica turno"}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2"><Label>Data</Label><Input type="date" data-testid="turno-data" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Ora inizio</Label><Input type="time" data-testid="turno-inizio" value={form.ora_inizio} onChange={(e) => setForm({ ...form, ora_inizio: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Ora fine</Label><Input type="time" data-testid="turno-fine" value={form.ora_fine} onChange={(e) => setForm({ ...form, ora_fine: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Distaccamento</Label><Input data-testid="turno-distaccamento" value={form.distaccamento} onChange={(e) => setForm({ ...form, distaccamento: e.target.value })} placeholder="Es. Roma Nord" /></div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Users className="h-4 w-4" /> Guardie assegnate ({form.guardie_ids.length})</Label>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input data-testid="turno-guardia-search" placeholder="Cerca per nome o CF" value={guardSearch} onChange={(e) => setGuardSearch(e.target.value)} className="pl-9" />
                    </div>
                    <Select value={guardDistacc} onValueChange={setGuardDistacc}>
                      <SelectTrigger data-testid="turno-guardia-distacc-filter"><SelectValue placeholder="Distaccamento" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tutti">Tutti i distaccamenti</SelectItem>
                        {distaccamenti.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="max-h-52 space-y-1 overflow-y-auto gz-scroll rounded-lg border border-slate-200 p-2" data-testid="turno-guardie-list">
                    {guardieFiltrate.length === 0 ? (
                      <p className="px-3 py-4 text-center text-sm text-slate-400">Nessuna guardia trovata.</p>
                    ) : guardieFiltrate.map((g) => {
                      const sel = form.guardie_ids.includes(g.id);
                      return (
                        <button key={g.id} type="button" data-testid={`turno-guardia-${g.id}`} onClick={() => toggleGuardia(g.id)}
                          className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors ${sel ? "bg-emerald-50 text-[#14382C]" : "hover:bg-slate-50 text-slate-600"}`}>
                          <span>{g.cognome} {g.nome} <span className="text-xs text-slate-400">— {g.distaccamento}</span></span>
                          <span className={`flex h-5 w-5 items-center justify-center rounded border ${sel ? "border-[#14382C] bg-[#14382C] text-white" : "border-slate-300"}`}>{sel && <Check className="h-3.5 w-3.5" />}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-2"><Label>Note</Label><Textarea rows={2} data-testid="turno-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
                <Button onClick={submit} disabled={saving} data-testid="save-turno-button" className="bg-[#14382C] hover:bg-[#0E281F]">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editingId ? "Salva modifiche" : "Pianifica"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <MonthPicker value={mese} onChange={setMese} testid="turni-month" />
        <div className="flex flex-wrap items-center gap-2">
          {["admin", "coordinatore"].includes(user?.role) && <ProvinciaFilter value={pdfProv} onChange={setPdfProv} testid="turni-provincia-filter" />}
          {canManage && <DistaccamentoFilter value={pdfDistacc} onChange={setPdfDistacc} testid="turni-distaccamento-filter" allLabel="Riepilogo: tutti i distaccamenti" />}
          <RiepilogoPdfButton mese={mese} distaccamento={pdfDistacc} provincia={pdfProv} testid="turni-riepilogo-pdf" />
        </div>
      </div>

      {turni.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-sm text-slate-400" data-testid="turni-empty">Nessun turno a {monthLabel(mese)}.</CardContent></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {turni.map((t) => (
            <Card key={t.id} data-testid={`turno-${t.id}`} className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-heading font-bold text-slate-900">{fmtDate(t.data)}</p>
                    <p className="flex items-center gap-1 text-sm text-slate-600"><Clock className="h-3.5 w-3.5" /> {t.ora_inizio} – {t.ora_fine}</p>
                  </div>
                  <Badge variant="outline" className={`${STATO_TURNO[t.stato]} font-medium capitalize`}>{t.stato}</Badge>
                </div>
                {t.distaccamento && <p className="mt-1 flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3 w-3" /> {t.distaccamento}</p>}
                {t.note && <p className="mt-1 text-xs text-slate-500">{t.note}</p>}

                <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                  {(t.guardie || []).map((ge) => {
                    const canAct = canManage || ge.guardia_id === myGuardiaId;
                    return (
                      <div key={ge.guardia_id} className="flex flex-wrap items-center justify-between gap-2" data-testid={`turno-member-${t.id}-${ge.guardia_id}`}>
                        <div className="text-sm">
                          <span className="font-medium text-slate-800">{ge.guardia_nome}</span>
                          {(ge.check_in || ge.check_out) && (
                            <span className="ml-2 text-xs text-slate-400">
                              {ge.check_in && `In ${fmtDateTime(ge.check_in)}`}{ge.check_out && ` · Out ${fmtDateTime(ge.check_out)}`}
                            </span>
                          )}
                        </div>
                        {canAct && (
                          <div className="flex items-center gap-1.5">
                            {!ge.check_in && <Button size="sm" className="h-7 bg-[#166534] px-2 hover:bg-[#14532d]" data-testid={`checkin-${t.id}-${ge.guardia_id}`} onClick={() => action(t.id, "checkin", ge.guardia_id)}><LogIn className="mr-1 h-3.5 w-3.5" /> Check-in</Button>}
                            {ge.check_in && !ge.check_out && <Button size="sm" variant="outline" className="h-7 px-2" data-testid={`checkout-${t.id}-${ge.guardia_id}`} onClick={() => action(t.id, "checkout", ge.guardia_id)}><LogOut className="mr-1 h-3.5 w-3.5" /> Check-out</Button>}
                            {ge.check_in && ge.check_out && <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">Completato</Badge>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {canManage && (
                  <div className="mt-3 flex items-center justify-end gap-1">
                    {t.finito && <span className="mr-auto inline-flex items-center gap-1 text-xs text-slate-600" data-testid={`turno-chiuso-${t.id}`}><Lock className="h-3.5 w-3.5" /> {t.modificabile ? "Turno concluso · modificabile solo da admin e coordinatori" : "Turno concluso · non più modificabile"}</span>}
                    {t.modificabile && (
                      <>
                        <Button size="sm" variant="ghost" className="text-[#14382C] hover:bg-emerald-50" data-testid={`edit-turno-${t.id}`} onClick={() => startEdit(t)}><Pencil className="mr-1.5 h-4 w-4" /> Modifica</Button>
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`delete-turno-${t.id}`} onClick={() => remove(t)}><Trash2 className="mr-1.5 h-4 w-4" /> Elimina</Button>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
